import { AlertTriangle, Chrome, MonitorDown, RefreshCcw, Settings, Smartphone, Trash2 } from "lucide-react";
import { useMetaTags } from "../components/MetaTags";

function Step({ n, title, children }: { n: number; title: string; children: React.ReactNode }) {
  return (
    <li className="flex gap-4">
      <div className="mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-indigo-600 text-sm font-bold text-white">
        {n}
      </div>
      <div>
        <h3 className="font-semibold text-slate-900">{title}</h3>
        <div className="mt-1 text-sm leading-relaxed text-slate-600">{children}</div>
      </div>
    </li>
  );
}

function Card({ icon: Icon, title, children }: { icon: typeof RefreshCcw; title: string; children: React.ReactNode }) {
  return (
    <section className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
      <div className="mb-3 flex items-center gap-2.5">
        <Icon size={20} className="text-indigo-600" />
        <h2 className="text-lg font-bold text-slate-900">{title}</h2>
      </div>
      {children}
    </section>
  );
}

export function DocsStuckVersion() {
  useMetaTags({
    title: "Fix a Stuck OmniCanvas App Version",
    description:
      "Troubleshooting steps for updating OmniCanvas when the installed web app or PWA is stuck on an old version.",
    url: "https://omnicanvasnotes.com/docs/troubleshooting-stuck-version",
  });

  return (
    <div className="pt-28 pb-20">
      <div className="mx-auto max-w-3xl px-6">
        <div className="mb-12 text-center">
          <p className="mb-3 text-xs font-bold uppercase tracking-[0.2em] text-indigo-600">
            Troubleshooting
          </p>
          <h1 className="mb-4 text-4xl font-extrabold tracking-tight text-slate-900">
            Fix a stuck app version
          </h1>
          <p className="mx-auto max-w-2xl text-lg text-slate-600">
            If OmniCanvas says you are on the latest version but Settings shows an old version,
            your browser or installed PWA may still be using a cached app shell.
          </p>
        </div>

        <div className="mb-8 rounded-2xl border border-amber-200 bg-amber-50 p-5">
          <div className="flex gap-3">
            <AlertTriangle size={22} className="mt-0.5 shrink-0 text-amber-600" />
            <div>
              <h2 className="font-bold text-amber-900">Your notes are not deleted by these steps</h2>
              <p className="mt-1 text-sm leading-relaxed text-amber-800">
                Clearing browser cache and unregistering the service worker refreshes the app code.
                If you use cloud sync, sign back in after the app reloads. If you work offline-only,
                export a backup first from OmniCanvas Settings &gt; Data.
              </p>
            </div>
          </div>
        </div>

        <div className="grid gap-5">
          <Card icon={Settings} title="First, try the built-in updater">
            <ol className="space-y-4">
              <Step n={1} title="Open Settings">
                In OmniCanvas, open <strong>Settings</strong>, then choose <strong>About</strong>.
              </Step>
              <Step n={2} title="Check for updates">
                Click <strong>Check for Updates</strong>. If an update is available, the button will
                change to an update/reload action.
              </Step>
              <Step n={3} title="Reload the app">
                Click the update/reload button and wait for the app to reopen. Then return to
                Settings &gt; About and confirm the version changed.
              </Step>
            </ol>
          </Card>

          <Card icon={Chrome} title="If Chrome or Edge is still stuck">
            <ol className="space-y-4">
              <Step n={1} title="Open DevTools">
                In the OmniCanvas tab, press <kbd className="rounded bg-slate-100 px-1.5 py-0.5">Cmd+Option+I</kbd>{" "}
                on Mac or <kbd className="rounded bg-slate-100 px-1.5 py-0.5">Ctrl+Shift+I</kbd> on Windows/Linux.
              </Step>
              <Step n={2} title="Unregister the service worker">
                Go to <strong>Application</strong> &gt; <strong>Service Workers</strong>, then click{" "}
                <strong>Unregister</strong> for OmniCanvas.
              </Step>
              <Step n={3} title="Clear site data">
                Go to <strong>Application</strong> &gt; <strong>Storage</strong>, then click{" "}
                <strong>Clear site data</strong>.
              </Step>
              <Step n={4} title="Hard reload">
                Reload the page with <kbd className="rounded bg-slate-100 px-1.5 py-0.5">Cmd+Shift+R</kbd>{" "}
                on Mac or <kbd className="rounded bg-slate-100 px-1.5 py-0.5">Ctrl+Shift+R</kbd> on Windows/Linux.
              </Step>
            </ol>
          </Card>

          <Card icon={MonitorDown} title="If the installed desktop PWA is stuck">
            <ol className="space-y-4">
              <Step n={1} title="Quit the OmniCanvas PWA window">
                Close all OmniCanvas app windows. On macOS, also quit it from the Dock if it is still running.
              </Step>
              <Step n={2} title="Clear data from the browser profile that installed it">
                Open the same browser you used to install the PWA, visit{" "}
                <strong>https://app.omnicanvasnotes.com</strong>, then follow the Chrome/Edge service-worker
                steps above.
              </Step>
              <Step n={3} title="Reopen or reinstall">
                Reopen the PWA. If it still shows the old version, uninstall the OmniCanvas PWA and install it
                again from <strong>https://app.omnicanvasnotes.com</strong>.
              </Step>
            </ol>
          </Card>

          <Card icon={Smartphone} title="If mobile Safari or Chrome is stuck">
            <div className="space-y-3 text-sm leading-relaxed text-slate-600">
              <p>
                On iPhone or iPad, remove the Home Screen app shortcut, then clear website data for
                OmniCanvas in Safari settings. After that, open OmniCanvas again and add it back to
                your Home Screen.
              </p>
              <p>
                On Android Chrome, open Chrome site settings for OmniCanvas, clear storage, then reopen
                the app and sign back in if needed.
              </p>
            </div>
          </Card>

          <Card icon={Trash2} title="Still seeing the old version?">
            <p className="text-sm leading-relaxed text-slate-600">
              Contact support at <a className="font-semibold text-indigo-600 hover:text-indigo-700" href="mailto:hello@omnicanvasnotes.com">hello@omnicanvasnotes.com</a>{" "}
              with the version shown in Settings &gt; About, your browser, operating system, and whether
              you are using the browser tab or installed PWA.
            </p>
          </Card>
        </div>
      </div>
    </div>
  );
}
